import type { Observable } from "rxjs";
import { merge as observableMerge, of as observableOf, Subject } from "rxjs";

import type { StateService } from "@uirouter/angularjs";
import type { ICacheFactoryService, ILogService, IRootScopeService } from "angular";
import { distinctUntilChanged, map, publishReplay, refCount, switchMap, tap } from "rxjs/operators";

import "angular-local-storage";
import "angular-translate";
import { locale } from "moment-timezone";

import { isEqual, some } from "lodash";

import type { IModalService } from "App/Modules/Services/FormContainer/Modal/IModalService";
import type { IProfileRepository, IProfileService, ITokenService } from "App/Modules/Auth";

import "../../Repositories/Profile/ProfileRepository";

import type {
    ILanguage,
    IPartnerUserSite,
    IPartnerUserSlim,
    IProfile,
    IResponse,
    IToken,
    IUserNotificationSettings,
    Permission,
} from "../../../Models";

const localStorageProfileKey = "profile";

export class ProfileService implements IProfileService {
    public static $inject = [
        "$log",
        "$translate",
        "mmTokenService",
        "mmInternalProfileRepository",
        "localStorageService",
        "TRANSLATE_MF_INTERPOLATION_CACHE",
        "$cacheFactory",
        "Idle",
        "$rootScope",
        "$uibModal",
        "$state",
        "mmModalService",
    ];

    private readonly profileUpdatesSubject: Subject<IProfile>;

    private readonly profileObservableInstance: Observable<IProfile>;

    private currentProfile: IProfile;

    private idleWarningModal: ng.ui.bootstrap.IModalInstanceService;

    constructor(
        private readonly $log: ILogService,
        private readonly $translate: angular.translate.ITranslateService,
        private readonly tokenService: ITokenService,
        private readonly profileRepository: IProfileRepository,
        private readonly localStorageService: angular.local.storage.ILocalStorageService,
        private readonly translateFormatCacheName: string,
        private readonly $cacheFactory: ICacheFactoryService,
        private readonly idle: ng.idle.IIdleService,
        private readonly $rootScope: IRootScopeService,
        private readonly $uibModal: ng.ui.bootstrap.IModalService,
        private readonly $state: StateService,
        private readonly modalService: IModalService,
    ) {
        this.profileUpdatesSubject = new Subject();
        this.profileObservableInstance = this.initProfileObservable();

        $rootScope.$on("IdleStart", () => {
            const autoLogoutTimes = this.calculateAutoLogoutTime(this.currentProfile.autoLogoutMinutes);
            this.idleWarningModal = this.$uibModal.open({
                template:
                    `${
                        '<div class="modal-header"><h3><i class="fal fa-exclamation-triangle"></i> {{' +
                        "'Gui.Common.DetectedAsIdle' | translate" +
                        "}}</h3>" +
                        '</div><div idle-countdown="countdown" class="modal-body">' +
                        "<p>{{'Gui.Common.YouHaveBeenDetectedAsIdleFormat' | translate: {countdown: countdown} }}</p>" +
                        '<uib-progressbar max="'
                    }${autoLogoutTimes.timeoutTime}"` +
                    `value="countdown" animate= "false" type="danger" class="progress-striped" > ` +
                    `</uib-progressbar></div>`,
            });
        });

        $rootScope.$on("IdleEnd", () => {
            this.idleWarningModal.close();
        });

        $rootScope.$on("IdleTimeout", () => {
            if (this.idleWarningModal) {
                this.idleWarningModal.close();
            }
            this.modalService.closeAll();
            this.tokenService.logout();
            this.$state.go("login");
            this.idle.unwatch();
        });
    }

    public getProfileIfAvailable(): IProfile {
        return this.currentProfile;
    }

    public profileObservable(): Observable<IProfile> {
        return this.profileObservableInstance;
    }

    public patch(profilePatch: IProfile): Observable<IResponse<IProfile>> {
        return this.profileRepository.patch(profilePatch).pipe(
            tap((profileResponse: IResponse<IProfile>) => {
                this.localStorageService.set(localStorageProfileKey, profileResponse.data);
                this.profileUpdatesSubject.next(profileResponse.data);
            }),
        );
    }

    public getLanguages(): Observable<IResponse<ILanguage[]>> {
        return this.profileRepository.getLanguages();
    }

    public getNotificationSettings(): Observable<IResponse<IUserNotificationSettings[]>> {
        return this.profileRepository.getNotificationSettings();
    }

    public getPartners(): Observable<IResponse<IPartnerUserSlim[]>> {
        return this.profileRepository.getPartners();
    }

    public getSites(): Observable<IResponse<IPartnerUserSite[]>> {
        return this.profileRepository.getSites();
    }

    public patchNotificationSettings(
        notificationSettings: IUserNotificationSettings[],
    ): Observable<IResponse<IUserNotificationSettings[]>> {
        return this.profileRepository.patchNotificationSettings(notificationSettings);
    }

    public hasPermission(permission: Permission): boolean {
        return (
            this.currentProfile &&
            (some(this.currentProfile.userPermissions.sitePermissions, (p) => p === permission) ||
                some(this.currentProfile.userPermissions.partnerPermissions, (p) => p === permission))
        );
    }

    private initProfileObservable(): Observable<IProfile> {
        return observableMerge(this.tokenService.tokenObservable(), this.profileUpdatesSubject).pipe(
            switchMap((tokenOrProfile) => {
                if (!tokenOrProfile) {
                    this.localStorageService.remove(localStorageProfileKey);
                    return observableOf<IProfile>(null);
                    // Temporary added validation profile for silent refresh from old to new version
                }
                if ((tokenOrProfile as IProfile) !== undefined && this.validateProfile(tokenOrProfile as IProfile)) {
                    return observableOf<IProfile>(tokenOrProfile as IProfile);
                }
                const token: IToken = tokenOrProfile as IToken;
                const localProfile: IProfile = this.localStorageService.get(localStorageProfileKey) as IProfile;
                // Temporary added validation profile for silent refresh from old to new version
                if (localProfile && localProfile.email === token.email && this.validateProfile(localProfile)) {
                    return observableOf(localProfile);
                }
                return this.profileRepository.get().pipe(
                    map((profileResponse: IResponse<IProfile>) => {
                        this.localStorageService.set(localStorageProfileKey, profileResponse.data);
                        return profileResponse.data;
                    }),
                );
            }),
            distinctUntilChanged<IProfile>((profile1, profile2) => isEqual(profile1, profile2)),
            tap((profile) => {
                this.currentProfile = profile;
                if (this.currentProfile) {
                    // const $cache = this.$cacheFactory.get(this.translateFormatCacheName);
                    // $cache.put(
                    //     this.currentProfile.language.id,
                    //     this.currentProfile.locale,
                    // );

                    if (this.currentProfile.autoLogoutMinutes > 0) {
                        const autoLogoutTimes = this.calculateAutoLogoutTime(this.currentProfile.autoLogoutMinutes);
                        this.idle.setIdle(autoLogoutTimes.idleTime);
                        this.idle.setTimeout(autoLogoutTimes.timeoutTime);
                        this.idle.watch();
                    }

                    locale(this.currentProfile.locale);
                    this.$translate.use(this.currentProfile.language.key);
                    this.localStorageService.set("locale", this.currentProfile.locale);
                }
                // this.$translate.refresh();
            }),
            publishReplay(1),
            refCount(),
        );
    }

    private validateProfile(profile: IProfile): boolean {
        return profile.language !== undefined && profile.email !== undefined;
    }

    private calculateAutoLogoutTime(autoLogoutMinutes: number): { idleTime: number; timeoutTime: number } {
        const autoLogout = {
            idleTime: 0,
            timeoutTime: 0,
        };

        if (autoLogoutMinutes > 5) {
            autoLogout.idleTime = autoLogoutMinutes - 5;
            autoLogout.timeoutTime = 5;
        } else {
            autoLogout.idleTime = autoLogoutMinutes / 4;
            autoLogout.timeoutTime = autoLogoutMinutes - autoLogout.idleTime;
        }

        autoLogout.idleTime *= 60;
        autoLogout.timeoutTime *= 60;

        return autoLogout;
    }
}
